import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  notification,
  Tabs,
  Input,
  Row,
  Col,
  DatePicker,
  Form,
  Tooltip,
  Modal,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageContentHeader from "../../../components/common/PageContentHeader";
import {
  getBulkUploadHistoryService,
  getDeletedVinHistoryService,
} from "../../../services/stock-services";
import dayjs from "dayjs";

const { TabPane } = Tabs;

const BulkUpdateHistory = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("bulk");
  const [bulkData, setBulkData] = useState([]);
  const [deleteData, setDeleteData] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkPagination, setBulkPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [deletePagination, setDeletePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  // const [bulkSearch, setBulkSearch] = useState('');
  const [deleteSearch, setDeleteSearch] = useState("");
  const [bulkStartDate, setBulkStartDate] = useState(null);
  const [bulkEndDate, setBulkEndDate] = useState(null);
  const [deleteStartDate, setDeleteStartDate] = useState(null);
  const [deleteEndDate, setDeleteEndDate] = useState(null);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  const fetchBulkHistory = async (
    page = bulkPagination.current,
    pageSize = bulkPagination.pageSize,
    startDate = bulkStartDate,
    endDate = bulkEndDate
  ) => {
    try {
      setBulkLoading(true);
      const params = {
        page,
        per_page: pageSize,
      };

      if (startDate) {
        params.start_date = startDate.format("YYYY-MM-DD");
      }
      if (endDate) {
        params.end_date = endDate.format("YYYY-MM-DD");
      }

      const response = await getBulkUploadHistoryService(params);
      if (response.data) {
        const historyData = response.data.data.list_data || [];
        setBulkData(historyData);
        setBulkPagination({
          current: page,
          pageSize,
          total: response.data.data.total_records || 0,
        });
      }
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const fetchDeleteHistory = async (
    page = deletePagination.current,
    pageSize = deletePagination.pageSize,
    searchTerm = deleteSearch,
    startDate = deleteStartDate,
    endDate = deleteEndDate
  ) => {
    try {
      setDeleteLoading(true);
      const params = {
        page,
        per_page: pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      if (startDate) {
        params.start_date = startDate.format("YYYY-MM-DD");
      }
      if (endDate) {
        params.end_date = endDate.format("YYYY-MM-DD");
      }

      const response = await getDeletedVinHistoryService(params);
      if (response.data) {
        const historyData = response.data.data.list_data || [];
        setDeleteData(historyData);
        setDeletePagination({
          current: page,
          pageSize,
          total: response.data.data.total_records || 0,
        });
      }
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "bulk") {
      setBulkPagination({ current: 1, pageSize: 10, total: 0 });
      fetchBulkHistory(1, 10, bulkStartDate, bulkEndDate);
    } else {
      setDeletePagination({ current: 1, pageSize: 10, total: 0 });
      fetchDeleteHistory(1, 10, deleteSearch, deleteStartDate, deleteEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleBulkTableChange = (paginationConfig) => {
    fetchBulkHistory(
      paginationConfig.current,
      paginationConfig.pageSize,
      bulkStartDate,
      bulkEndDate
    );
  };

  const handleDeleteTableChange = (paginationConfig) => {
    fetchDeleteHistory(
      paginationConfig.current,
      paginationConfig.pageSize,
      deleteSearch,
      deleteStartDate,
      deleteEndDate
    );
  };

  // const handleBulkSearch = (value) => {
  //   setBulkSearch(value);
  //   fetchBulkHistory(1, bulkPagination.pageSize, value?.trim());
  // };

  const handleDeleteSearch = (value) => {
    setDeleteSearch(value);
    fetchDeleteHistory(
      1,
      deletePagination.pageSize,
      value?.trim(),
      deleteStartDate,
      deleteEndDate
    );
  };

  const handleBulkStartDateChange = (date) => {
    setBulkStartDate(date);
    if (date && bulkEndDate && date.isAfter(bulkEndDate)) {
      notification.error({
        description: "Start Date cannot be after End Date.",
      });
      return;
    }
    fetchBulkHistory(1, bulkPagination.pageSize, date, bulkEndDate);
  };

  const handleBulkEndDateChange = (date) => {
    setBulkEndDate(date);
    if (date && bulkStartDate && date.isBefore(bulkStartDate)) {
      notification.error({
        description: "End Date cannot be before Start Date.",
      });
      return;
    }
    fetchBulkHistory(1, bulkPagination.pageSize, bulkStartDate, date);
  };

  const handleDeleteStartDateChange = (date) => {
    setDeleteStartDate(date);
    if (date && deleteEndDate && date.isAfter(deleteEndDate)) {
      notification.error({
        description: "Start Date cannot be after End Date.",
      });
      return;
    }
    fetchDeleteHistory(
      1,
      deletePagination.pageSize,
      deleteSearch,
      date,
      deleteEndDate
    );
  };

  const handleDeleteEndDateChange = (date) => {
    setDeleteEndDate(date);
    if (date && deleteStartDate && date.isBefore(deleteStartDate)) {
      notification.error({
        description: "End Date cannot be before Start Date.",
      });
      return;
    }
    fetchDeleteHistory(
      1,
      deletePagination.pageSize,
      deleteSearch,
      deleteStartDate,
      date
    );
  };

  const handleDownloadReport = (path) => {
    if (path) {
      const link = document.createElement("a");
      link.href = path;
      link.target = "_blank";
      link.download = "bulk_upload_report.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: "Report downloaded successfully",
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    // Format: "01 July 2025"
    return dayjs(dateString).format("DD MMMM YYYY");
  };

  // Disable future dates
  const disableFutureDates = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const bulkColumns = [
    {
      title: "File Name",
      dataIndex: "file_name",
      key: "file_name",
      width: "25%",
      render: (text) => text || "Bulk Update File.xlsx",
    },
    {
      title: "Date of Update",
      dataIndex: "created_at",
      key: "created_at",
      width: "20%",
      render: (date) => formatDate(date),
    },
    {
      title: "Updated By",
      dataIndex: "created_by",
      key: "created_by",
      width: "20%",
      render: (user) => user || "-",
    },
    {
      title: "Result Summary",
      key: "resultSummary",
      width: "25%",
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: "4px" }}>
            Add = {record.new_added_records || 0}
          </div>
          <div style={{ marginBottom: "4px" }}>
            Update = {record.updated_records || 0}
          </div>
          <div style={{ marginBottom: "4px" }}>
            Failed = {record.failed_records || 0}
          </div>
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: "auto" }}
          onClick={() => handleDownloadReport(record.uploaded_file_path)}
          disabled={!record.uploaded_file_path}
        >
          Download Report
        </Button>
      ),
    },
  ];

  const deleteColumns = [
    {
      title: "Chassis Number",
      dataIndex: "chassis_number",
      key: "chassis_number",
      width: "25%",
      render: (text) => text || "-",
    },
    {
      title: "Date of Delete",
      dataIndex: "deleted_at",
      key: "deleted_at",
      width: "25%",
      render: (date) => formatDate(date),
    },
    {
      title: "Deleted By",
      dataIndex: "deleted_by_name",
      key: "deleted_by_name",
      width: "25%",
      render: (user) => user || "-",
    },
    {
      title: "Reason",
      dataIndex: "delete_remarks",
      key: "delete_remarks",
      width: "25%",
      render: (text) => {
        if (!text || text === "-") return "-";
        
        const maxLength = 50;
        const isLongText = text.length > maxLength;
        const truncatedText = isLongText ? `${text.substring(0, maxLength)}...` : text;

        return (
          <div style={{ wordBreak: "break-word" }}>
            <Tooltip title={text} placement="topLeft">
              <span>{truncatedText}</span>
            </Tooltip>
            {isLongText && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginLeft: 8, height: "auto" }}
                onClick={() => {
                  setSelectedReason(text);
                  setReasonModalVisible(true);
                }}
              >
                See more
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="user-data">
      <PageContentHeader
        title=""
        headerRight={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin/stock")}
            type="text"
          >
            Back
          </Button>
        }
      />
      <div className="personal-detail">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Bulk Update History" key="bulk">
            <Card className="filter-card mb-3">
              <Form layout="vertical">
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="start_date" label="Start Date">
                      <DatePicker
                        format="DD/MM/YYYY"
                        onChange={handleBulkStartDateChange}
                        value={bulkStartDate}
                        placeholder="Start Date"
                        style={{ width: "100%" }}
                        className="form-control-ant"
                        allowClear
                        disabledDate={disableFutureDates}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="end_date" label="End Date">
                      <DatePicker
                        format="DD/MM/YYYY"
                        onChange={handleBulkEndDateChange}
                        value={bulkEndDate}
                        placeholder="End Date"
                        style={{ width: "100%" }}
                        className="form-control-ant"
                        allowClear
                        disabledDate={disableFutureDates}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
              <Table
                loading={bulkLoading}
                size="middle"
                columns={bulkColumns}
                dataSource={bulkData}
                rowKey={(record) => record.id}
                pagination={{
                  ...bulkPagination,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} records`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                onChange={handleBulkTableChange}
                scroll={{
                  x: "max-content", // 🟢 makes it responsive horizontally
                }}
              />
          </TabPane>
          <TabPane tab="Delete History" key="delete">
            <Card className="filter-card mb-3">
              <Form layout="vertical">
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item name="search" label="Search (By Chassis No.)">
                      <Input.Search
                        placeholder="Search by chassis number"
                        allowClear
                        value={deleteSearch}
                        onChange={(e) => setDeleteSearch(e.target.value)}
                        onSearch={handleDeleteSearch}
                        onPressEnter={(e) => handleDeleteSearch(e.target.value)}
                        className="form-control-ant"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="start_date" label="Start Date">
                      <DatePicker
                        format="DD/MM/YYYY"
                        onChange={handleDeleteStartDateChange}
                        value={deleteStartDate}
                        placeholder="Start Date"
                        style={{ width: "100%" }}
                        className="form-control-ant"
                        allowClear
                        disabledDate={disableFutureDates}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="end_date" label="End Date">
                      <DatePicker
                        format="DD/MM/YYYY"
                        onChange={handleDeleteEndDateChange}
                        value={deleteEndDate}
                        placeholder="End Date"
                        style={{ width: "100%" }}
                        className="form-control-ant"
                        allowClear
                        disabledDate={disableFutureDates}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
              <Table
                loading={deleteLoading}
                size="middle"
                columns={deleteColumns}
                dataSource={deleteData}
                rowKey={(record) => record.id}
                pagination={{
                  ...deletePagination,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} records`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                onChange={handleDeleteTableChange}
                scroll={{
                  x: "max-content", // 🟢 makes it responsive horizontally
                }}
              />
          </TabPane>
        </Tabs>
      </div>
      <Modal
        title="Delete Reason"
        open={reasonModalVisible}
        onCancel={() => {
          setReasonModalVisible(false);
          setSelectedReason("");
        }}
        footer={[
          <Button key="close" onClick={() => {
            setReasonModalVisible(false);
            setSelectedReason("");
          }}>
            Close
          </Button>,
        ]}
        width={600}
      >
        <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {selectedReason}
        </p>
      </Modal>
    </div>
  );
};

export default BulkUpdateHistory;
